import { canViewField } from '@supply-mover-2-0/shared';
import type { SelectedObject } from '../types';

/**
 * The one rule that decides whether a list column can carry data — used by
 * BOTH sides so the offer and the delivery cannot drift apart:
 *
 *   • the server (`ListFieldHydrationService`) uses it to decide which
 *     configured columns to load and merge onto the rows;
 *   • the list configurator uses it to decide which fields to offer.
 *
 * Keeping one implementation is the whole point. When the picker offered
 * fields from database metadata while each endpoint decided delivery on its
 * own, any list whose endpoint returned a narrower shape silently advertised
 * columns that could only ever render "-" (PD-15409).
 */

/**
 * Whether a list's rows can be traced back to records of `object`, which is
 * what makes loading extra columns for them possible.
 *
 * Requires an EXPLICIT `rowIdField`. Defaulting to `id` would be wrong often
 * enough to be dangerous: plenty of lists declare an object they display
 * values from without their rows being records of it — the vendor-items rows
 * carry a vendor_item id while declaring `item`, the competitive-price rows
 * carry a competitive_price id while declaring `client`. Loading a table by
 * some other table's id doesn't render blank, it renders the WRONG record, so
 * a list has to say which key holds the id before anything is loaded for it.
 *
 * @param object - One entry from a list config's `objects[]`.
 * @param deliveredKeys - Keys present on the rows the endpoint returned.
 */
export function isObjectHydratable(
	object: SelectedObject,
	deliveredKeys: ReadonlySet<string>,
): boolean {
	const rowIdField = object.rowIdField;

	if (rowIdField == null || rowIdField === '') {
		return false;
	}

	// A nested path (`item.id`) is identified by its root key, which is what
	// the rows carry.
	const rootKey = rowIdField.split('.')[0];

	return rootKey != null && deliveredKeys.has(rootKey);
}

export interface HydratableFieldsInput {
	/** The object being hydrated, from the list config's `objects[]`. */
	object: SelectedObject;
	/**
	 * Field names the list's active configuration asks for, as
	 * `<objectTable>.<fieldName>` or bare names (an authored `requiredFields`
	 * array uses bare names for the list's primary object).
	 */
	requiredFields: ReadonlyArray<string>;
	/**
	 * Every name a real column of `object.objectTable` can be addressed by.
	 *
	 * Include BOTH the entity's property names and its database column names:
	 * the field picker is populated from `_object_field`, which stores database
	 * names, so a config can legitimately reference `fk_warehouse__id` for a
	 * column the entity calls `warehouseId`.
	 */
	columnNames: ReadonlySet<string>;
	/** Keys already present on the rows the endpoint returned. */
	deliveredKeys: ReadonlySet<string>;
	/** Permission check for the current user. */
	can: (permission: string) => boolean;
}

/**
 * The fields this object can contribute to the rows: configured by the active
 * list, not already delivered, a real column, and visible to the caller.
 *
 * Order is stable (first configured occurrence wins) so callers can use the
 * result directly in a SELECT and in test assertions.
 */
export function resolveHydratableFields(
	input: HydratableFieldsInput,
): Array<string> {
	const { object, requiredFields, columnNames, deliveredKeys, can } = input;

	if (!isObjectHydratable(object, deliveredKeys)) {
		return [];
	}

	const fields: Array<string> = [];
	const seen = new Set<string>();

	for (const required of requiredFields) {
		const fieldName = resolveFieldName(required, object.objectTable);

		if (fieldName == null || seen.has(fieldName)) {
			continue;
		}

		if (deliveredKeys.has(fieldName)) {
			continue;
		}

		if (!columnNames.has(fieldName)) {
			continue;
		}

		if (!canViewField(fieldName, can)) {
			continue;
		}

		seen.add(fieldName);
		fields.push(fieldName);
	}

	return fields;
}

/**
 * The bare field name a required-field entry refers to for `objectTable`, or
 * null when the entry belongs to a different table.
 *
 * A bare entry (no dot) comes from a config's authored `requiredFields` and
 * refers to the list's own object. A prefixed entry names its table
 * explicitly. Custom fields keep their `cf:` prefix, which is part of the key
 * the backend aliases onto the row.
 */
function resolveFieldName(
	required: string,
	objectTable: string,
): string | null {
	if (required == null || required === '') {
		return null;
	}

	if (required.startsWith('cf:')) {
		return required;
	}

	const separator = required.indexOf('.');

	if (separator < 0) {
		return required;
	}

	const table = required.substring(0, separator);
	const fieldName = required.substring(separator + 1);

	if (table !== objectTable || fieldName === '') {
		return null;
	}

	return fieldName;
}
